/* eslint-disable no-underscore-dangle */
import _ from "lodash";
import { liteClient } from "algoliasearch/lite";

export const enhancer = (element) => {
    const algoliaAppId = element.getAttribute("data-algolia-application-id");
    const algoliaApiKey = element.getAttribute("data-algolia-app-key");
    const algoliaResultsIndex = element.getAttribute("data-algolia-results-index");
    const algoliaWordsIndex = element.getAttribute("data-algolia-words-index");
    element.removeAttribute("data-algolia-application-id");
    element.removeAttribute("data-algolia-app-key");
    element.removeAttribute("data-algolia-results-index");
    element.removeAttribute("data-algolia-words-index");

    if (!algoliaAppId || !algoliaApiKey || !algoliaResultsIndex || !algoliaWordsIndex) return;

    // Initialize Algolia
    const searchClient = liteClient(
        algoliaAppId,
        algoliaApiKey
    );

    const searchInput = document.getElementById("content-search-form");
    const searchForm = document.querySelector(".js-search-form");
    const filters = document.querySelectorAll(".js-search-filters__input");
    const filterLabels = document.querySelectorAll(".js-search-filters__label");
    const resultsContainer = document.querySelector(".js-search-results-list");
    const resultsLiveRegion = document.querySelector(".js-search-results__live-region");
    const wordExplanationContainer = document.querySelector(".js-explanation-container");
    const hitTemplate = document.querySelector(".js-search-result-item-template").innerHTML;
    const wordExplanationTemplate = document.querySelector(".js-explanation-template").innerHTML;

    // activeHits is stored in a variable to prevent unnecessary API calls
    let activeHits = [];
    let activeWordHits = [];

    /**
     * Function to get filtered hits based on the selected filter
     */
    function getFilteredHits() {
        const selectedFilter = Array.from(filters).find(
            (radio) => radio.checked
        );
        return selectedFilter.value === "all"
          ? activeHits
          : activeHits.filter((hit) => hit.group === selectedFilter.value);
    }

    /**
     * Function to update filter labels
     */
    function updateFilterLabels() {
        const counts = activeHits.reduce((acc, hit) => {
            const group = hit.group; // Get the group value from the hit

            // Initialize the count for the group if it doesn't already exist
            if (!acc[group]) {
                acc[group] = 0;
            }
            // Increment the group's count
            acc[group] += 1;

            return acc;
        }, {});

        filterLabels.forEach((label) => {
            const group = label.getAttribute("for");
            const count = group === "all"
              ? activeHits.length + activeWordHits.length
              : counts[group] || 0;
            const input = Array.from(filters).find(
                (radio) => radio.value === group
            );

            label.querySelector(".search-filters__label-amount").textContent = count;

            if (count === 0) {
                input.setAttribute("disabled", true);
            } else {
                input.removeAttribute("disabled");
            }
        });
    };

    /**
     * Function to update live region content
     */
    function updateLiveRegion() {
        const filteredHits = getFilteredHits();
        const selectedFilter = Array.from(filters).find(
            (radio) => radio.checked
        );
        const count = selectedFilter.value === "all"
          ? filteredHits.length + activeWordHits.length
          : filteredHits.length;
        const noResultsText = resultsLiveRegion.getAttribute("data-no-results-text");
        const message = count === 0
          ? noResultsText
          : `Er zijn ${count} resultaten gevonden.`;

        // setTimeout is used to prevent the live region result from being taken over by a screen reader message
        setTimeout(() => {
          resultsLiveRegion.textContent = message;
        }, 1000);
    };

    /**
     * Function to display word explanation above search results
     */
    function displayWordExplanation() {
        if (activeWordHits.length === 0) {
            wordExplanationContainer.innerHTML = "";
            return;
        };

        const wordExplanation = activeWordHits.map((hit) => {
            const title = hit._highlightResult.name.value;
            const content = hit._snippetResult.content.value;
            const showReadMoreText = content.startsWith('…') || content.endsWith('…');

            const html = wordExplanationTemplate
                .replace(/%url%/g, hit.url)
                .replace(/%title%/g, title)
                .replace(/%excerpt%/g, content)
                .replace(/%name-link%/g, hit.name)
                .replace(/%show-read-more%/g, showReadMoreText ? "true" : "false");
            return html;
        }).join("");

        wordExplanationContainer.innerHTML = wordExplanation;
    }

    /**
     * Function to display search results
     */
    function displayResults() {
        const filteredHits = getFilteredHits();
        const noResultsText = resultsLiveRegion.getAttribute("data-no-results-text");

        updateFilterLabels();
        updateLiveRegion();
        displayWordExplanation();

        if (filteredHits.length === 0 && activeWordHits.length === 0) {
            resultsContainer.innerHTML =
                `<p class='index__no-result'>${noResultsText}</p>`;
        }
        if (filteredHits.length === 0 && activeWordHits.length > 0) {
            resultsContainer.innerHTML = "";
        }
        if (filteredHits.length > 0) {
            const resultsHtml = filteredHits.map((hit) => {
                const title = hit._highlightResult.name.value;
                const content = hit._snippetResult.content.value;
                const html = hitTemplate
                    .replace(/%title%/g, title)
                    .replace(/%content%/g, content)
                    .replace(/%url%/g, hit.url)
                    .replace(/%illustration%/g, hit.figure);
                return html;
            }).join("");

            resultsContainer.innerHTML = resultsHtml;
        }
    }

    /**
     * Function to search Algolia
     */
    function search() {
        const query = searchInput.value;

        if (query.length === 0) {
            activeHits = [];
            activeWordHits = [];
            displayResults();
            return;
        };

        searchClient
            .search({
                requests: [{
                    indexName: algoliaResultsIndex,
                    query,
                    hitsPerPage: 500,
                },
                {
                  indexName: algoliaWordsIndex,
                  query,
                  hitsPerPage: 1,
                  restrictSearchableAttributes: ["name"],
              }],
            })
            .then(({ results }) => {
                activeHits = results[0].hits;
                activeWordHits = results[1].hits;
                document.title = `${results[0].query} zoekresultaten  | Waarzitwatin | Rijksoverheid`;
                displayResults();
            })
            .catch((err) => {
                console.error(err);
                resultsContainer.innerHTML = "<p>Er is iets fout gegaan bij het zoeken.</p>";
            });
    };

    /**
     * Function to turn url query parameters into search input
     */
    function urlToSearch() {
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get("query");

        if (query) {
            searchInput.value = query;
            search();
        }
    };

    // Event listeners
    searchForm.addEventListener("submit", (e) => {
        e.preventDefault();
        search();
    });
    searchInput.addEventListener("input", _.debounce(search, 400));
    filters.forEach((filter) => {
        filter.addEventListener("change", displayResults);
    });
    window.onload = (event) => urlToSearch();
};
